// src/providers/progress-provider.tsx
import { BProgress, css } from "@bprogress/core";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef
} from "react";
var ProgressContext = createContext(
  void 0
);
var useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error("useProgress must be used within a ProgressProvider");
  }
  return context;
};
var ProgressProvider = ({
  children,
  color = "#0A2FFF",
  height = "2px",
  options,
  spinnerPosition = "top-right",
  style,
  disableStyle = false,
  nonce,
  shallowRouting = false,
  disableSameURL = true,
  startPosition = 0,
  delay = 0,
  stopDelay = 0
}) => {
  const timer = useRef(null);
  const isAutoStopDisabled = useRef(false);
  const disableAutoStop = useCallback(
    () => isAutoStopDisabled.current = true,
    []
  );
  const enableAutoStop = useCallback(
    () => isAutoStopDisabled.current = false,
    []
  );
  const start = useCallback(
    (startPosition2 = 0, delay2 = 0, autoStopDisabled = false) => {
      if (autoStopDisabled) disableAutoStop();
      timer.current = setTimeout(() => {
        if (startPosition2 > 0) BProgress.set(startPosition2);
        BProgress.start();
      }, delay2);
    },
    [disableAutoStop]
  );
  const stop = useCallback(
    (stopDelay2 = 0, forcedStopDelay = 0) => {
      setTimeout(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          if (!BProgress.isStarted()) return;
          BProgress.done();
          if (isAutoStopDisabled.current) enableAutoStop();
        }, stopDelay2);
      }, forcedStopDelay);
    },
    [enableAutoStop]
  );
  const inc = useCallback((amount) => BProgress.inc(amount), []);
  const dec = useCallback((amount) => BProgress.dec(amount), []);
  const set = useCallback((n) => BProgress.set(n), []);
  const pause = useCallback(() => BProgress.pause(), []);
  const resume = useCallback(() => BProgress.resume(), []);
  const getOptions = useCallback(() => BProgress.settings, []);
  const setOptions = useCallback(
    (newOptions) => {
      const currentOptions = getOptions();
      const updates = typeof newOptions === "function" ? newOptions(currentOptions) : newOptions;
      const nextOptions = { ...currentOptions, ...updates };
      BProgress.configure(nextOptions);
    },
    [getOptions]
  );
  const styles = useMemo(
    () => /* @__PURE__ */ React.createElement("style", { nonce }, style || css({
      color,
      height,
      spinnerPosition
    })),
    [color, height, nonce, spinnerPosition, style]
  );
  BProgress.configure(options || {});
  return /* @__PURE__ */ React.createElement(
    ProgressContext.Provider,
    {
      value: {
        start,
        stop,
        inc,
        dec,
        set,
        pause,
        resume,
        setOptions,
        getOptions,
        isAutoStopDisabled,
        disableAutoStop,
        enableAutoStop,
        shallowRouting,
        disableSameURL,
        startPosition,
        delay,
        stopDelay
      }
    },
    !disableStyle ? styles : null,
    children
  );
};

// src/hooks/use-anchor-progress.tsx
import { useEffect, useRef as useRef2 } from "react";
import {
  isSameURL,
  isSameURLWithoutSearch,
  getAnchorProperty
} from "@bprogress/core";
function useAnchorProgress({
  shallowRouting = false,
  disableSameURL = true,
  startPosition = 0,
  delay = 0,
  stopDelay = 0,
  targetPreprocessor,
  disableAnchorClick = false,
  startOnLoad = false,
  forcedStopDelay = 0
}, deps = []) {
  const elementsWithAttachedHandlers = useRef2([]);
  const timerRef = useRef2(null);
  const { start, stop, isAutoStopDisabled } = useProgress();
  useEffect(() => {
    if (startOnLoad) start(startPosition, delay);
  }, []);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!isAutoStopDisabled.current) stop();
    }, stopDelay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, deps);
  useEffect(() => {
    if (disableAnchorClick) return;
    const handleAnchorClick = (event) => {
      if (event.defaultPrevented) return;
      const anchorElement = event.currentTarget;
      if (anchorElement.hasAttribute("download")) return;
      const target = event.target;
      let preventProgress = target?.getAttribute("data-prevent-progress") === "true" || anchorElement?.getAttribute("data-prevent-progress") === "true";
      if (!preventProgress) {
        let element = target;
        while (element && element.tagName.toLowerCase() !== "a") {
          if (element.parentElement?.getAttribute("data-prevent-progress") === "true") {
            preventProgress = true;
            break;
          }
          element = element.parentElement;
        }
      }
      if (preventProgress) return;
      const anchorTarget = getAnchorProperty(anchorElement, "target");
      if (anchorTarget === "_blank") return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const targetHref = getAnchorProperty(anchorElement, "href");
      const targetUrl = targetPreprocessor ? targetPreprocessor(new URL(targetHref)) : new URL(targetHref);
      const currentUrl = new URL(location.href);
      if (shallowRouting && isSameURLWithoutSearch(targetUrl, currentUrl) && disableSameURL)
        return;
      if (isSameURL(targetUrl, currentUrl) && disableSameURL) return;
      start(startPosition, delay);
    };
    const handleMutation = () => {
      const anchorElements = Array.from(document.querySelectorAll("a"));
      const validAnchorElements = anchorElements.filter((anchor) => {
        const href = getAnchorProperty(anchor, "href");
        const isBProgressDisabled = anchor.getAttribute("data-disable-progress") === "true";
        const isNotTelOrMailto = href && !href.startsWith("tel:") && !href.startsWith("mailto:") && !href.startsWith("blob:") && !href.startsWith("javascript:");
        return !isBProgressDisabled && isNotTelOrMailto && getAnchorProperty(anchor, "target") !== "_blank";
      });
      validAnchorElements.forEach((anchor) => {
        anchor.addEventListener("click", handleAnchorClick, true);
      });
      elementsWithAttachedHandlers.current = validAnchorElements;
    };
    const mutationObserver = new MutationObserver(handleMutation);
    mutationObserver.observe(document, { childList: true, subtree: true });
    const originalWindowHistoryPushState = window.history.pushState;
    window.history.pushState = new Proxy(window.history.pushState, {
      apply: (target, thisArg, argArray) => {
        if (!isAutoStopDisabled.current) stop(stopDelay, forcedStopDelay);
        return target.apply(thisArg, argArray);
      }
    });
    return () => {
      mutationObserver.disconnect();
      elementsWithAttachedHandlers.current.forEach((anchor) => {
        anchor.removeEventListener("click", handleAnchorClick, true);
      });
      elementsWithAttachedHandlers.current = [];
      window.history.pushState = originalWindowHistoryPushState;
    };
  }, [
    disableAnchorClick,
    targetPreprocessor,
    shallowRouting,
    disableSameURL,
    delay,
    stopDelay,
    startPosition,
    start,
    stop,
    forcedStopDelay,
    isAutoStopDisabled
  ]);
}

// src/components/progress.tsx
import React6 from "react";

// src/utils/classnames.ts
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// src/components/bar.tsx
import React2 from "react";
function BarInner({ as, children, className, classSelector = "bar", ...rest }, ref) {
  const Component = as ?? "div";
  return React2.createElement(
    Component,
    {
      ref,
      className: classNames(classSelector, className),
      ...rest
    },
    children
  );
}
var Bar = React2.forwardRef(BarInner);

// src/components/peg.tsx
import React3 from "react";
function PegInner({ as, children, className, classSelector = "peg", ...rest }, ref) {
  const Component = as ?? "div";
  return React3.createElement(
    Component,
    {
      ref,
      className: classNames(classSelector, className),
      ...rest
    },
    children
  );
}
var Peg = React3.forwardRef(PegInner);

// src/components/spinner.tsx
import React4 from "react";
function SpinnerInner({
  as,
  children,
  className,
  classSelector = "spinner",
  ...rest
}, ref) {
  const Component = as ?? "div";
  return React4.createElement(
    Component,
    {
      ref,
      className: classNames(classSelector, className),
      ...rest
    },
    children
  );
}
var Spinner = React4.forwardRef(SpinnerInner);

// src/components/spinner-icon.tsx
import React5 from "react";
function SpinnerIconInner({
  as,
  children,
  className,
  classSelector = "spinner-icon",
  ...rest
}, ref) {
  const Component = as ?? "div";
  return React5.createElement(
    Component,
    {
      ref,
      className: classNames(classSelector, className),
      ...rest
    },
    children
  );
}
var SpinnerIcon = React5.forwardRef(SpinnerIconInner);

// src/components/progress.tsx
function ProgressInner({ as, children, className, style, ...rest }, ref) {
  const Component = as ?? "div";
  return React6.createElement(
    Component,
    {
      ref,
      className: classNames("bprogress", className),
      style: { ...style, display: "none" },
      ...rest
    },
    children || /* @__PURE__ */ React6.createElement(React6.Fragment, null, /* @__PURE__ */ React6.createElement(Bar, null, /* @__PURE__ */ React6.createElement(Peg, null)), /* @__PURE__ */ React6.createElement(Spinner, null, /* @__PURE__ */ React6.createElement(SpinnerIcon, null)))
  );
}
var Progress = React6.forwardRef(ProgressInner);

// src/components/indeterminate.tsx
import React7 from "react";
function IndeterminateInner({
  as,
  className,
  classSelector = "indeterminate",
  ...rest
}, ref) {
  const Component = as ?? "div";
  return React7.createElement(
    Component,
    {
      ref,
      className: classNames(classSelector, className),
      ...rest
    },
    React7.createElement("div", { className: "inc" }),
    React7.createElement("div", { className: "dec" })
  );
}
var Indeterminate = React7.forwardRef(IndeterminateInner);

// src/utils/with-suspense.tsx
import React8, { Suspense } from "react";
function withSuspense(Component) {
  return function WithSuspenseComponent(props) {
    return /* @__PURE__ */ React8.createElement(Suspense, null, /* @__PURE__ */ React8.createElement(Component, { ...props }));
  };
}

// src/utils/with-memo.tsx
import { memo } from "react";
function shallowCompareProps(prevProps, nextProps, ignoreKeys = []) {
  const prevKeys = Object.keys(prevProps).filter(
    (key) => !ignoreKeys.includes(key)
  );
  const nextKeys = Object.keys(nextProps).filter(
    (key) => !ignoreKeys.includes(key)
  );
  if (prevKeys.length !== nextKeys.length) {
    return false;
  }
  for (const key of prevKeys) {
    if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }
  return true;
}
function withMemo(Component, ignoreKeys = ["memo", "shouldCompareComplexProps"]) {
  return memo(Component, (prevProps, nextProps) => {
    if (nextProps.memo === false) return false;
    if (!nextProps.shouldCompareComplexProps) return true;
    return shallowCompareProps(prevProps, nextProps, ignoreKeys);
  });
}
export {
  Bar,
  Indeterminate,
  Peg,
  Progress,
  ProgressProvider,
  Spinner,
  SpinnerIcon,
  useAnchorProgress,
  useProgress,
  withMemo,
  withSuspense
};
